import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, track, formatApiError } from "@/lib/api";

export const fieldCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30 focus:border-[#1E40AF] transition";

async function submitLead(payload) {
  await api.post("/leads", payload);
  track("lead_submit", { type: payload.type, study_level: payload.study_level || "", destination: payload.destination || "" });
}

const QUALIFICATIONS = ["Class 12", "Bachelor's", "Master's", "Diploma", "Other"];
const DESTINATIONS = ["Romania", "United Kingdom", "Germany", "Italy", "France", "Ireland", "Other Europe"];

export function HeroEnquiryCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", qualification: "", study_level: "Bachelor's", destination: "Romania" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, type: "enquiry" });
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="hero-enquiry-card" className="relative bg-white/90 backdrop-blur-2xl border border-white/60 shadow-[0_24px_60px_rgba(8,40,110,0.22)] rounded-2xl p-6 sm:p-8">
      <div className="absolute -top-3 left-6 bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
        Free Profile Assessment
      </div>
      <h3 className="text-xl font-extrabold text-[#0B192C] tracking-tight">START YOUR EUROPE JOURNEY</h3>
      <p className="text-sm text-slate-500 mt-1 mb-5">Tell us your profile. A counsellor maps your options.</p>
      <form data-testid="hero-enquiry-card-form" onSubmit={onSubmit} className="space-y-3.5">
        <input data-testid="hero-input-fullname" required placeholder="Full Name" className={fieldCls} value={form.name} onChange={set("name")} />
        <input data-testid="hero-input-whatsapp" required placeholder="Phone / WhatsApp Number" className={fieldCls} value={form.phone} onChange={set("phone")} />
        <input data-testid="hero-input-email" type="email" placeholder="Email Address" className={fieldCls} value={form.email} onChange={set("email")} />
        <select data-testid="hero-select-qualification" required className={fieldCls} value={form.qualification} onChange={set("qualification")}>
          <option value="" disabled>Highest Qualification</option>
          {QUALIFICATIONS.map((q) => <option key={q}>{q}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="hero-select-level" className={fieldCls} value={form.study_level} onChange={set("study_level")}>
            <option>Bachelor's</option>
            <option>Master's</option>
          </select>
          <select data-testid="hero-select-destination" className={fieldCls} value={form.destination} onChange={set("destination")}>
            {DESTINATIONS.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <button
          data-testid="hero-submit-assessment-btn"
          disabled={loading}
          className="w-full py-3.5 rounded-xl text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? "SUBMITTING..." : "GET MY OPTIONS"} <ArrowRight className="w-4 h-4" />
        </button>
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Lock className="w-3 h-3" /> 100% free &amp; confidential. No spam, ever.
        </p>
      </form>
    </div>
  );
}

export function ContactForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", qualification: "", study_level: "Bachelor's", destination: "Romania", course: "", intake: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const prefill = sessionStorage.getItem("prefill_course");
    if (prefill) {
      setForm((f) => ({ ...f, course: prefill }));
      sessionStorage.removeItem("prefill_course");
    }
    const handler = (e) => setForm((f) => ({ ...f, course: e.detail }));
    window.addEventListener("prefill-course", handler);
    return () => window.removeEventListener("prefill-course", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, type: "counselling" });
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="contact-input-name" required placeholder="Full Name" className={fieldCls} value={form.name} onChange={set("name")} />
        <input data-testid="contact-input-phone" required placeholder="Phone / WhatsApp" className={fieldCls} value={form.phone} onChange={set("phone")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="contact-input-email" type="email" placeholder="Email Address" className={fieldCls} value={form.email} onChange={set("email")} />
        <input data-testid="contact-input-city" placeholder="Your City" className={fieldCls} value={form.city} onChange={set("city")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select data-testid="contact-select-qualification" required className={fieldCls} value={form.qualification} onChange={set("qualification")}>
          <option value="" disabled>Highest Qualification</option>
          {QUALIFICATIONS.map((q) => <option key={q}>{q}</option>)}
        </select>
        <select data-testid="contact-select-level" className={fieldCls} value={form.study_level} onChange={set("study_level")}>
          <option>Bachelor's</option>
          <option>Master's</option>
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select data-testid="contact-select-intake" className={fieldCls} value={form.intake} onChange={set("intake")}>
          <option value="" disabled>Preferred Intake</option>
          <option>Autumn 2026</option>
          <option>Spring 2027</option>
          <option>Autumn 2027</option>
          <option>Not sure yet</option>
        </select>
        <select data-testid="contact-select-country" className={fieldCls} value={form.destination} onChange={set("destination")}>
          {DESTINATIONS.map((d) => <option key={d}>{d}</option>)}
        </select>
      </div>
      <input data-testid="contact-input-course" placeholder="Preferred Course / Field" className={fieldCls} value={form.course} onChange={set("course")} />
      <textarea data-testid="contact-input-message" rows={4} placeholder="Your message (optional)" className={fieldCls} value={form.message} onChange={set("message")} />
      <button
        data-testid="contact-form-submit-btn"
        disabled={loading}
        className="w-full py-4 rounded-xl text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/25 active:scale-[0.98] transition-all disabled:opacity-60"
      >
        {loading ? "BOOKING..." : "BOOK MY FREE COUNSELLING"}
      </button>
    </form>
  );
}

const ASSESSMENT_STEPS = [
  { key: "qualification", label: "Highest Qualification", type: "select", options: QUALIFICATIONS },
  { key: "percentage", label: "Percentage / CGPA", type: "input", placeholder: "e.g. 78% or 8.5 CGPA" },
  { key: "course", label: "Study Field", type: "select", options: ["Medicine", "Engineering", "Computer Science", "Business & Management", "Architecture", "Life Sciences", "Social Sciences", "Finance", "International Relations", "Data Science", "Other"] },
  { key: "destination", label: "Preferred Destination", type: "select", options: DESTINATIONS },
  { key: "budget", label: "Yearly Budget", type: "select", options: ["Under €3,000", "€3,000 - €5,000", "€5,000 - €8,000", "Above €8,000", "Need guidance"] },
  { key: "intake", label: "Preferred Intake", type: "select", options: ["Autumn 2026", "Spring 2027", "Autumn 2027", "Not sure yet"] },
  { key: "contact", label: "Contact Details", type: "contact" },
];

export function AssessmentForm() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ qualification: "", percentage: "", course: "", destination: "Romania", budget: "", intake: "", name: "", phone: "", email: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const current = ASSESSMENT_STEPS[step];
  const isLast = step === ASSESSMENT_STEPS.length - 1;

  const valid = () => {
    if (current.type === "contact") return form.name && form.phone && form.email;
    return Boolean(form[current.key]);
  };

  const next = async () => {
    if (!valid()) {
      toast.error("Please complete this step to continue.");
      return;
    }
    if (!isLast) {
      setStep(step + 1);
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, type: "assessment", study_level: form.qualification === "Class 12" ? "Bachelor's" : "Master's" });
      track("assessment_complete");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="assessment-success" className="text-center py-12">
        <CheckCircle2 className="w-14 h-14 text-[#059669] mx-auto mb-5" />
        <h3 className="text-2xl font-extrabold text-[#0B192C]">THANK YOU!</h3>
        <p className="text-slate-600 mt-3 max-w-md mx-auto">
          Your profile has been submitted to Harwell Global Education.
          <strong className="block mt-1">A counsellor will contact you regarding suitable study options.</strong>
        </p>
      </div>
    );
  }

  return (
    <div data-testid="assessment-form" className="max-w-xl mx-auto">
      <div className="flex items-center gap-2 mb-8">
        {ASSESSMENT_STEPS.map((s, i) => (
          <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#1E40AF]" : "bg-slate-200"}`} />
        ))}
      </div>
      <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-2">
        Step {step + 1} of {ASSESSMENT_STEPS.length}
      </p>
      <h3 className="text-xl font-extrabold text-[#0B192C] mb-5">{current.label}</h3>

      {current.type === "select" && (
        <select data-testid={`assessment-input-${current.key}`} className={fieldCls} value={form[current.key]} onChange={set(current.key)}>
          <option value="" disabled>Select an option</option>
          {current.options.map((o) => <option key={o}>{o}</option>)}
        </select>
      )}
      {current.type === "input" && (
        <input data-testid={`assessment-input-${current.key}`} placeholder={current.placeholder} className={fieldCls} value={form[current.key]} onChange={set(current.key)} />
      )}
      {current.type === "contact" && (
        <div className="space-y-3.5">
          <input data-testid="assessment-input-name" placeholder="Full Name" className={fieldCls} value={form.name} onChange={set("name")} />
          <input data-testid="assessment-input-phone" placeholder="Phone / WhatsApp" className={fieldCls} value={form.phone} onChange={set("phone")} />
          <input data-testid="assessment-input-email" type="email" placeholder="Email Address" className={fieldCls} value={form.email} onChange={set("email")} />
        </div>
      )}

      <div className="flex items-center justify-between mt-8">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          data-testid="assessment-back-btn"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 disabled:opacity-0"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={next}
          disabled={loading}
          data-testid="assessment-next-btn"
          className="px-7 py-3 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-60"
        >
          {isLast ? (loading ? "SUBMITTING..." : "CHECK MY OPTIONS") : "CONTINUE"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
